import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { BLOCK_EMBED, embed } from '@gitbook/doc-core';
import type { Block } from '@gitbook/slate';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Submit,
    TextField,
    TextInput,
    useDialogClose,
} from '@gitbook/spine-react';
import { normalizeUrl } from '@gitbook/util-url';

import { ContentEditorContext } from '../contexts';

/**
 * Modal to insert an embed in the document.
 */
export function InsertEmbedDialog(props: { onInsert: (node: Block) => void }) {
    const { onInsert } = props;
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Embed a URL</Heading>
                <Description>
                    Insert a URL to embed a live preview. Works with YouTube, Google Drive, Vimeo,
                    and more.
                </Description>
            </DialogHeader>
            <InsertEmbedForm onInsert={onInsert} />
        </Dialog>
    );
}

type InsertEmbedFormInputs = {
    url: string;
};

function InsertEmbedForm(props: { onInsert: (node: Block) => void }) {
    const { onInsert } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const close = useDialogClose();

    const form = useForm<InsertEmbedFormInputs>({
        defaultValues: { url: '' },
    });

    const onSubmit: SubmitHandler<InsertEmbedFormInputs> = async (data) => {
        const normalizedUrl = normalizeUrl(data.url);

        onInsert(embed.createEmbedBlock(normalizedUrl));

        logAnalyticsEvent({
            type: 'edit_page_document_insert_block',
            from: 'embed',
            blockType: BLOCK_EMBED,
            embedHostname: new URL(normalizedUrl).hostname,
        });

        close();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField name="url" validate="http-url">
                    <TextInput autoFocus placeholder="Paste a URL or type it" type="url" />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Insert" />
            </DialogFooter>
        </Form>
    );
}
